import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Config } from '@environments/config';
import { capitalCase } from '@local/ts-infra';
import * as markdownItAttrs from '@marked-it/markdown-it-attrs';
import hljs from 'highlight.js';
import MarkdownIt from 'markdown-it';
import * as markdownItBracketedSpan from 'markdown-it-bracketed-spans';
import { full as markdownItEmoji } from 'markdown-it-emoji';
import * as markdownItTaskLists from 'markdown-it-task-lists';
import DOMPurify from 'dompurify';

@Injectable({
  providedIn: 'root',
})
export class ResultMarkdownService {
  private markdownIt: MarkdownIt;

  constructor(private sanitizer: DomSanitizer) {
    this.init();
  }

  render(markdown: string, markdownData?: any): string {
    const res = this.markdownIt.render(markdown, markdownData);
    return DOMPurify.sanitize(res, { ADD_TAGS: ['inline-reference-item', 'answer-code-header'], ADD_ATTR: ['target', 'rel'] });
  }

  isUncloseCode(text: string) {
    return (text.match(/```/g) || []).length % 2 !== 0;
  }

  renderUncloseCode(text: string) {
    const esc = this.markdownIt.utils.escapeHtml;
    const codeHeader = '<answer-code-header></answer-code-header>';
    return `${codeHeader}<pre class="hljs"><code class="hljs">${esc(text)}</code></pre>`;
  }

  private init() {
    const positionCounters = new Map<string, number>();
    this.markdownIt = new MarkdownIt({
      breaks: true,
      html: true,
      linkify: true,
      typographer: true,
      highlight: (str, lang) => {
        if (lang && hljs.getLanguage(lang)) {
          try {
            const highlighted = hljs.highlight(lang, str, true).value;
            const languageLabel = `<answer-code-header data-code-lang='${capitalCase(lang)}'></answer-code-header>`;
            return `${languageLabel}<div class="code-content"><pre class="hljs language-${lang}"><code class="hljs language-${lang} ${lang}">${highlighted}</code></pre></div>`;
          } catch (__) {}
        }
        const esc = this.markdownIt.utils.escapeHtml;
        const codeHeader = '<answer-code-header></answer-code-header>';
        return `${codeHeader}<pre class="hljs"><code class="hljs">${esc(str)}</code></pre>`;
      },
    }).enable(['link']);

    const defaultRender =
      this.markdownIt.renderer.rules.link_open ||
      function (tokens, idx, options, _env, self) {
        return self.renderToken(tokens, idx, options);
      };

    this.markdownIt.use(markdownItAttrs).use(markdownItBracketedSpan).use(markdownItEmoji).use(markdownItTaskLists, {
      enabled: true,
      label: true,
    });

    this.markdownIt.inline.ruler.before('emphasis', 'custom_reference_item', function (state, silent) {
      positionCounters.clear();
      if (!['chat-answer', 'result-answer', 'external-chat-answer', 'chat-agentic'].includes(state.env?.sourceType)) {
        return;
      }

      const start = state.pos;
      const marker = state.src.charCodeAt(start);

      if (marker !== 0x25 || state.src.charCodeAt(start + 1) !== 0x25) {
        return false;
      }

      const match = state.src.slice(start).match(/^%%(.*?)%%/);

      if (!match) return false;

      const content = match[1];

      if (silent) {
        return false;
      }

      const token = state.push('custom_reference_item', 'i', 0);
      token.content = content;
      token.meta = state.env;
      state.pos += match[0].length;

      return true;
    });

    this.markdownIt.renderer.rules.custom_reference_item = function (tokens, index) {
      const token = tokens[index];
      const idIndex = Number(token.content);
      if (!token.meta.items?.length) {
        return `<inline-reference-item data-attr='${JSON.stringify({ idIndex: idIndex, searchId: token.meta.searchId })}'></inline-reference-item>`;
      }
      if (idIndex < 1 || idIndex > token.meta.items.length) {
        return;
      }
      let id: string;
      const item = token.meta.items[idIndex - 1];
      if (token.meta.sourceType === 'chat-answer') {
        id = item?.externalId;
      } else if (['result-answer', 'external-chat-answer'].includes(token.meta.sourceType)) {
        id = item?.id;
      } else if (token.meta.sourceType === 'chat-agentic') {
        id = item?.externalId || item?.id;
      }
      if (!id) {
        return;
      }

      const counterKey = `${token.meta.searchId}-${idIndex}`;
      const currentPosition = positionCounters.get(counterKey) || 0;
      positionCounters.set(counterKey, currentPosition + 1);

      const dataAttr = JSON.stringify({
        id,
        sourceType: token.meta.sourceType,
        idIndex,
        answerId: token.meta.identity,
        searchId: token.meta.searchId,
        position: currentPosition,
      });

      return `<inline-reference-item data-attr='${dataAttr}' ></inline-reference-item>`;
    };

    this.markdownIt.renderer.rules.link_open = (tokens, idx, options, env, self) => {
      const hrefIndex = tokens[idx].attrIndex('href');

      if (hrefIndex >= 0) {
        const href = tokens[idx].attrs[hrefIndex][1];
        const targetIndex = tokens[idx].attrIndex('target');

        if (href && href.startsWith('sandbox:')) {
          tokens[idx].attrPush(['class', 'sandbox-link']);
          tokens[idx].attrPush(['data-sandbox-path', href]);
          tokens[idx].attrs[hrefIndex][1] = 'javascript:void(0);';

          if (targetIndex >= 0) {
            tokens[idx].attrs.splice(targetIndex, 1);
          }
        } else {
          if (targetIndex < 0) {
            tokens[idx].attrPush(['target', '_blank']);
          } else {
            tokens[idx].attrs[targetIndex][1] = '_blank';
          }
        }
      }

      return defaultRender(tokens, idx, options, env, self);
    };
    const defaultRenderImage =
      this.markdownIt.renderer.rules.image ||
      function (tokens, idx, options, _env, self) {
        return self.renderToken(tokens, idx, options);
      };

    this.markdownIt.renderer.rules.image = function (tokens, idx, options, env, self) {
      const token = tokens[idx];
      const src = token.attrGet('src');
      const title = token.attrGet('content') || '';
      const titleAttr = title ? ` title="${title}"` : '';
      const content = token.content || token.attrGet('alt') || '';

      if (src && src.startsWith('sandbox:')) {
        const dataAttr = `data-sandbox-path="${src}"`;
        return `<a href="javascript:void(0);" class="sandbox-link" ${dataAttr}${titleAttr}>${content}</a>`;
      }
      if (src) {
        const cloudinaryUrl = `${Config.cloudinaryImageUrl}/f_auto,q_auto/${encodeURIComponent(src)}`;

        token.attrSet('src', cloudinaryUrl);
      }
      return defaultRenderImage(tokens, idx, options, env, self);
    };
  }
}
